import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "cvf_auth_token";

const AuthContext = createContext(null);

/** Extract UTM/gclid params from the current URL so we can attach them to
 *  registration (foundation for the M8 attribution dashboard). */
function readAttribution() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const pick = (k) => params.get(k) || undefined;
  const attr = {
    utm_source: pick("utm_source"),
    utm_medium: pick("utm_medium"),
    utm_campaign: pick("utm_campaign"),
    utm_term: pick("utm_term"),
    utm_content: pick("utm_content"),
    gclid: pick("gclid"),
  };
  const hasAny = Object.values(attr).some(Boolean);
  return hasAny ? attr : null;
}

/** Normalise FastAPI error responses (string, object, or 422 detail array). */
export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ") || fallback;
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return fallback;
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null,
  );
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!token);

  // Attach token to every axios request while it exists
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [token]);

  // Rehydrate user from server on mount (or after token change)
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setUser(null);
      setLoading(false);
      return () => {};
    }
    setLoading(true);
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!cancelled) setUser(data);
      })
      .catch(() => {
        if (!cancelled) {
          // Token is invalid — clear it
          window.localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const persistSession = useCallback((nextToken, nextUser) => {
    window.localStorage.setItem(STORAGE_KEY, nextToken);
    setToken(nextToken);
    setUser(nextUser);
  }, []);

  const register = useCallback(
    async ({ email, password, full_name, phone, marketing_consent }) => {
      const attribution = readAttribution();
      const { data } = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name,
        phone: phone || undefined,
        marketing_consent: !!marketing_consent,
        attribution: attribution || undefined,
      });
      persistSession(data.token, data.user);
      return data.user;
    },
    [persistSession],
  );

  const login = useCallback(
    async ({ email, password }) => {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      persistSession(data.token, data.user);
      return data.user;
    },
    [persistSession],
  );

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_) {
      /* ignore */
    }
    window.localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const resendVerification = useCallback(async (email) => {
    await axios.post(`${API}/auth/resend-verification`, { email });
  }, []);

  const verifyEmail = useCallback(
    async (verificationToken) => {
      const { data } = await axios.post(`${API}/auth/verify-email`, { token: verificationToken });
      // Refresh /me if we're logged in so is_verified flips
      if (token) {
        try {
          const me = await axios.get(`${API}/auth/me`);
          setUser(me.data);
        } catch (_) {
          /* ignore */
        }
      }
      return data;
    },
    [token],
  );

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      isAuthenticated: !!token && !!user,
      register,
      login,
      logout,
      resendVerification,
      verifyEmail,
    }),
    [token, user, loading, register, login, logout, resendVerification, verifyEmail],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
